import React from "react";
import { Team, PathToCourt1, Tournament } from "../types/tournament";
import { calculatePathToCourt1 } from "../utils/tournamentLogic";
import { Target, TrendingUp, Users, Clock } from "lucide-react";

interface PathToCourt1TrackerProps {
  teams: Team[];
  tournament?: Tournament;
}

const PathToCourt1Tracker: React.FC<PathToCourt1TrackerProps> = ({
  teams,
  tournament,
}) => {
  const paths = teams.map((team) => calculatePathToCourt1(team, teams));

  const getPathColor = (path: PathToCourt1) => {
    if (path.currentPosition === 1) return "bg-yellow-100 border-yellow-300";
    if (path.currentPosition <= 3) return "bg-green-100 border-green-300";
    if (path.currentPosition <= 6) return "bg-blue-100 border-blue-300";
    // Check if mathematically eliminated (too many teams above with more wins)
    const teamsAbove = teams.filter(
      (t) => t.currentCourt < path.currentPosition
    );
    const totalRounds = tournament?.totalRounds || 6;
    const currentRound = tournament?.currentRound || 1;
    const canStillReach =
      teamsAbove.length <= (totalRounds - currentRound + 1) * 2;
    return canStillReach
      ? "bg-gray-100 border-gray-300"
      : "bg-red-100 border-red-300";
  };

  const getPathIcon = (path: PathToCourt1) => {
    if (path.currentPosition === 1) return "👑";
    if (path.currentPosition <= 3) return "🎯";
    if (path.currentPosition <= 6) return "📈";
    return "🏃";
  };

  const getDifficultyText = (path: PathToCourt1) => {
    if (path.currentPosition === 1) return "Champion Position";
    if (path.currentPosition <= 3) return "Close to Victory";
    if (path.currentPosition <= 6) return "Making Progress";
    return "Long Road Ahead";
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2" />
          Path to Court 1
        </h3>
        <p className="text-gray-600 mb-6">
          Each team's journey to reach the championship position
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paths
          .sort((a, b) => a.currentPosition - b.currentPosition)
          .map((path) => (
            <div
              key={path.team.id}
              className={`border-2 rounded-lg p-6 ${getPathColor(path)}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{getPathIcon(path)}</span>
                  <div>
                    <h4 className="font-bold text-lg">{path.team.name}</h4>
                    <p className="text-sm text-gray-600">
                      {path.team.player1} & {path.team.player2}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">
                    #{path.currentPosition}
                  </div>
                  <div className="text-sm text-gray-600">Current</div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status:</span>
                  <span className="text-sm font-semibold">
                    {getDifficultyText(path)}
                  </span>
                </div>

                {path.currentPosition > 1 && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Games to Win:</span>
                      <span className="text-sm font-semibold text-green-600">
                        {path.gamesNeeded}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Est. Rounds:</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {path.estimatedRounds}
                      </span>
                    </div>

                    {path.opponentsToBeat.length > 0 && (
                      <div className="mt-4">
                        <div className="text-sm font-medium mb-2">
                          Teams to Beat:
                        </div>
                        <div className="space-y-1">
                          {path.opponentsToBeat
                            .slice(0, 3)
                            .map((opponent, index) => (
                              <div
                                key={opponent.id}
                                className="flex items-center justify-between text-sm"
                              >
                                <span>
                                  #{index + 1} {opponent.name}
                                </span>
                                <span className="text-gray-500">
                                  Court {opponent.currentCourt}
                                </span>
                              </div>
                            ))}
                          {path.opponentsToBeat.length > 3 && (
                            <div className="text-sm text-gray-500">
                              +{path.opponentsToBeat.length - 3} more teams
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {path.currentPosition === 1 && (
                  <div className="mt-4 p-3 bg-yellow-200 rounded-lg">
                    <div className="text-center">
                      <div className="text-lg font-bold text-yellow-800">
                        👑 Champion!
                      </div>
                      <div className="text-sm text-yellow-700">
                        Defending Court 1 for {path.team.court1Streak} round
                        {path.team.court1Streak !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {path.currentPosition > 1 && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Court {path.currentPosition}</span>
                    <span>Court 1</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.max(
                          5,
                          ((path.currentPosition - 1) /
                            Math.max(...teams.map((t) => t.currentCourt))) *
                            100
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Path to Court 1 Summary
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {paths.filter((p) => p.currentPosition === 1).length}
            </div>
            <div className="text-sm text-gray-600">Champions</div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {
                paths.filter(
                  (p) => p.currentPosition <= 3 && p.currentPosition > 1
                ).length
              }
            </div>
            <div className="text-sm text-gray-600">Close to Victory</div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {
                paths.filter(
                  (p) => p.currentPosition <= 6 && p.currentPosition > 3
                ).length
              }
            </div>
            <div className="text-sm text-gray-600">Making Progress</div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">
              {paths.filter((p) => p.currentPosition > 6).length}
            </div>
            <div className="text-sm text-gray-600">Long Road Ahead</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathToCourt1Tracker;
